import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { lazy, Suspense, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { areasQuery, type AreaGroupBy } from '@/api/queries'
import { EmptyState, PageHeader } from '@/components/page'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { MapPoint } from '@/features/map/mapData'
import { AmpelBadge } from '@/features/orders/AmpelBadge'
import { AreaBundle } from '@/features/orders/AreaBundle'
import { AMPEL_COLOR, ampelOf } from '@/features/orders/ampel'
import { GROUP_BY } from '@/features/orders/filters'
import { requireNav } from '@/nav/guard'

const PointsMap = lazy(() =>
  import('@/features/map/PointsMap').then((m) => ({ default: m.PointsMap })),
)

export const Route = createFileRoute('/_app/areas')({
  beforeLoad: requireNav('areas'),
  validateSearch: z.object({ groupBy: z.enum(GROUP_BY).optional().catch(undefined) }),
  component: AreasPage,
})

/** Gebietsansicht (A-28): Aufträge je Gebiet mit offenen Aufträgen, Fristen und offenen Gruben, als Liste und Karte. */
function AreasPage() {
  const { t, i18n } = useTranslation()
  const { groupBy = 'rajon' } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const areas = useQuery(areasQuery(groupBy))
  const [bundleFor, setBundleFor] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [mapFailed, setMapFailed] = useState(false)

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    )

  const points = useMemo<MapPoint[]>(
    () =>
      (areas.data ?? []).flatMap((area) =>
        area.center && area.key
          ? [
              {
                id: area.key,
                lng: area.center.coordinates[0] ?? 0,
                lat: area.center.coordinates[1] ?? 0,
                color: AMPEL_COLOR[ampelOf(area.nextDeadlineAt)],
                // Fläche wächst mit der Zahl offener Aufträge (8 bis 28 px)
                radius: Math.min(28, 8 + area.openCount * 2),
              },
            ]
          : [],
      ),
    [areas.data],
  )
  const withoutCenter = (areas.data ?? []).filter((a) => !a.center || !a.key).length

  const ordersLink = (key: string) => ({ groupBy, area: key })

  return (
    <>
      <PageHeader title={t('areas.title')} description={t('areas.description')} />
      <div className="mb-4 grid max-w-xs gap-1">
        <label htmlFor="areas-group" className="text-sm font-medium">
          {t('orders.groupBy')}
        </label>
        <select
          id="areas-group"
          className="h-11 rounded-md border border-input bg-card px-3 text-base"
          value={groupBy}
          onChange={(e) => void navigate({ search: { groupBy: e.target.value as AreaGroupBy } })}
        >
          {GROUP_BY.map((value) => (
            <option key={value} value={value}>
              {t(`orders.groupByValue.${value}`)}
            </option>
          ))}
        </select>
      </div>

      {notice && (
        <p role="status" className="mb-4 rounded-md border bg-card p-3">
          {notice}
        </p>
      )}

      {areas.isPending ? (
        <Skeleton className="h-64 w-full" aria-label={t('common.loading')} />
      ) : areas.isError ? (
        <EmptyState title={t('areas.loadError')} body={t('errors.network')} />
      ) : (
        <div className="grid items-start gap-6 xl:grid-cols-2">
          <section aria-labelledby="areas-list">
            <h2 id="areas-list" className="mb-2 text-lg font-medium">
              {t('areas.listTitle')}
            </h2>
            <div className="overflow-x-auto rounded-lg border bg-card">
              <table className="w-full text-left" aria-label={t('areas.title')}>
                <thead className="bg-muted text-sm">
                  <tr>
                    <th scope="col" className="px-4 py-2">
                      {t('orders.areaName')}
                    </th>
                    <th scope="col" className="px-4 py-2">
                      {t('orders.areaOpen')}
                    </th>
                    <th scope="col" className="px-4 py-2">
                      {t('orders.areaNext')}
                    </th>
                    <th scope="col" className="px-4 py-2">
                      {t('orders.areaPits')}
                    </th>
                    <th scope="col" className="px-4 py-2">
                      <span className="sr-only">{t('review.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {areas.data.map((area) => (
                    <tr key={area.key} className="border-t align-top" data-area={area.key}>
                      <th scope="row" className="px-4 py-2 font-medium">
                        {area.key || t('areas.noArea')}
                      </th>
                      <td className="px-4 py-2">
                        {area.openCount}
                        <span className="text-sm text-muted-foreground"> / {area.totalCount}</span>
                      </td>
                      <td className="px-4 py-2">
                        {area.nextDeadlineAt ? (
                          <>
                            <AmpelBadge ampel={ampelOf(area.nextDeadlineAt)} />
                            <div className="text-sm text-muted-foreground">
                              {formatDate(area.nextDeadlineAt)}
                            </div>
                          </>
                        ) : (
                          t('common.none')
                        )}
                      </td>
                      <td className="px-4 py-2">{area.openPits}</td>
                      <td className="px-4 py-2">
                        {area.key && (
                          <div className="flex flex-wrap gap-2">
                            <Button asChild variant="outline" size="sm">
                              <Link
                                to="/orders"
                                search={ordersLink(area.key)}
                                aria-label={t('areas.showOrders', { key: area.key })}
                              >
                                {t('orders.areaFilter')}
                              </Link>
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              aria-label={t('areas.bundleFor', { key: area.key })}
                              onClick={() => setBundleFor(area.key)}
                            >
                              {t('areas.bundle')}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section aria-labelledby="areas-map">
            <h2 id="areas-map" className="mb-2 text-lg font-medium">
              {t('areas.mapTitle')}
            </h2>
            {mapFailed ? (
              <p role="alert" className="text-destructive">
                {t('map.error')}
              </p>
            ) : (
              <Suspense
                fallback={
                  <Skeleton className="h-[28rem] w-full" aria-label={t('common.loading')} />
                }
              >
                <PointsMap
                  points={points}
                  cluster={false}
                  testId="areas-map"
                  label={t('areas.mapLabel')}
                  onSelect={(key) => void navigate({ to: '/orders', search: ordersLink(key) })}
                  onError={() => setMapFailed(true)}
                />
              </Suspense>
            )}
            {withoutCenter > 0 && (
              <p className="mt-2 text-sm text-muted-foreground">{t('areas.noLocation')}</p>
            )}
          </section>
        </div>
      )}

      {bundleFor && (
        <AreaBundle
          groupBy={groupBy}
          areaKey={bundleFor}
          onClose={() => setBundleFor(null)}
          onDone={(message) => setNotice(message)}
        />
      )}
    </>
  )
}
